import { useMemo } from 'react';
import Spinner from '@rio-cloud/rio-uikit/Spinner';
import SmoothScrollbars from '@rio-cloud/rio-uikit/SmoothScrollbars';
import isEmpty from 'lodash/fp/isEmpty';

import { ChatListItem } from '../components/ChatListItem';
import { useAppContext } from '../layout/AppContext';
import { useChatError, useGroupChannels, useIsInitialized } from '../features/chat/chatSlice';
import {
    useFilteredChannels,
    useFilteredChats,
    useFilteredTotalUnreadMessageCount,
} from '../features/fleetGroupFilter/useFilteredChats';
import { EmptyChatList } from '../features/chatList/EmptyChatList';
import { ChatLoadingError } from '../features/chat/ChatLoadingError';
import { ChatListHeader } from '../features/newChatList/ChatListHeader';
import { Footer } from '../features/footer/Footer';

export const ChatListViewPage = () => {
    const { navigateToChat } = useAppContext();

    const groupChannels = useGroupChannels();
    const { chats } = useFilteredChats();
    const fleetFilteredChannels = useFilteredChannels(groupChannels);
    const totalUnreadMessageCount = useFilteredTotalUnreadMessageCount(groupChannels);
    const isInitialized = useIsInitialized();
    const hasErrors = useChatError();

    const handleSelectChat = (channelId: string) => {
        navigateToChat(channelId, 'from chat list page');
    };

    const filteredChannels = useMemo(
        () => fleetFilteredChannels.filter(channel => channel.lastMessage),
        [fleetFilteredChannels]
    );
    const hasOpenChannels = useMemo(() => groupChannels.some(channel => channel.lastMessage), [groupChannels]);

    const showSpinner = !isInitialized && !hasErrors;
    const showChannels = isInitialized && !isEmpty(filteredChannels);
    const showEmptyScreen = isInitialized && isEmpty(filteredChannels) && !showSpinner;

    if (hasErrors) {
        return <ChatLoadingError errorType={hasErrors.errorType} errorText={hasErrors.errorText} />;
    }

    return (
        <div className='display-flex flex-column height-100vh'>
            <ChatListHeader
                chats={chats}
                openChatsCount={filteredChannels.length}
                showFleetGroupFilter={hasOpenChannels}
                totalUnreadMessageCount={totalUnreadMessageCount}
            />
            <div className='flex-1-1'>
                {showSpinner && <Spinner />}

                {showChannels && (
                    <SmoothScrollbars>
                        <div className='border border-bottom-only divider-y-1 divider-color-light divider-style-solid overflow-y-auto no-scrollbars position-relative'>
                            {filteredChannels.map(groupChannel => (
                                <ChatListItem
                                    key={groupChannel.channelId}
                                    id={groupChannel.channelId}
                                    type={groupChannel.channelId.split('_')[0]}
                                    messageCount={groupChannel.unreadMessageCount}
                                    lastMessage={groupChannel.lastMessage}
                                    onClick={() => handleSelectChat(groupChannel.channelId)}
                                />
                            ))}
                        </div>
                    </SmoothScrollbars>
                )}

                {showEmptyScreen && (
                    <div className='no-scrollbars position-relative'>
                        <EmptyChatList />
                    </div>
                )}
            </div>
            <Footer />
        </div>
    );
};
